import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

const styles = require('./Modal.module.scss');

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  children: React.ReactNode;
  className?: string;
  dismissible?: boolean;
  footer?: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  triggerId?: string;
}

const Modal: React.FC<ModalProps> = ({
  ariaLabel,
  ariaLabelledBy,
  children,
  className,
  dismissible = true,
  footer,
  isOpen: isOpenProp = false,
  onClose,
  onOpen,
  triggerId,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const modalRef = useRef<HTMLDivElement>(null);
  const dismissibleRef = useRef(dismissible);
  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);

  useEffect(() => {
    dismissibleRef.current = dismissible;
    onCloseRef.current = onClose;
    onOpenRef.current = onOpen;
  }, [dismissible, onClose, onOpen]);

  useEffect(() => {
    if (isOpen) onOpenRef.current?.();
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (!dismissibleRef.current) return;
    setIsOpen(false);
    onCloseRef.current?.();
  }, []);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  // Open from a non-React trigger (Rails-rendered button etc.).
  useEffect(() => {
    if (!triggerId) return undefined;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return undefined;

    const handleOpen = () => setIsOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  // Body scroll lock + focus trap + restore.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => modalRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={classNames(styles.modal, className)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
      >
        <div className={styles.content}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
